import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  useGetFieldDefinitionsQuery,
  useDeleteFieldDefinitionMutation,
} from '../../app/services/customFields';
import ModalAddCustomField from '../ModalAddCustomField';
import ModalDelete from '../ModalDelete';

export default function CustomFieldsManager({ maintenanceId, maintenanceName }) {
  const { t } = useTranslation();
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [editField, setEditField] = useState(null);
  const [deleteField, setDeleteField] = useState(null);

  const { data: fieldDefinitions = [], isLoading, refetch } = useGetFieldDefinitionsQuery(
    maintenanceId,
    { skip: !maintenanceId }
  );

  const [deleteFieldDefinition] = useDeleteFieldDefinitionMutation();

  const getFieldTypeLabel = (type) => {
    const types = {
      numeric: t('customFields.numeric'),
      text: t('customFields.text'),
      checkbox: t('customFields.checkbox'),
      radio: t('customFields.radio'),
    };
    return types[type] || type;
  };

  const handleEdit = (field) => {
    setEditField(field);
    setOpenAddModal(true);
  };

  const handleDelete = (field) => {
    setDeleteField(field);
    setOpenDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await deleteFieldDefinition(deleteField.id, { maintenanceId }).unwrap();
      setOpenDeleteModal(false);
      setDeleteField(null);
      refetch();
    } catch (error) {
      console.error('Error deleting field:', error);
    }
  };

  const handleCloseAddModal = () => {
    setOpenAddModal(false);
    setEditField(null);
  };

  if (isLoading) {
    return <Typography>{t('generic.loading')}</Typography>;
  }

  return (
    <Box sx={{ mt: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          {t('customFields.title')} - {maintenanceName}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenAddModal(true)}
        >
          {t('customFields.addField')}
        </Button>
      </Box>

      {fieldDefinitions.length === 0 ? (
        <Paper sx={{ p: 2 }}>
          <Typography color="text.secondary">
            {t('customFields.noFieldsDefined')}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('customFields.fieldName')}</TableCell>
                <TableCell>{t('customFields.fieldType')}</TableCell>
                <TableCell>{t('customFields.required')}</TableCell>
                <TableCell>{t('customFields.unit')}</TableCell>
                <TableCell>{t('customFields.options')}</TableCell>
                <TableCell align="right">{t('generic.action')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {fieldDefinitions.map((field) => (
                <TableRow key={field.id}>
                  <TableCell>{field.field_name}</TableCell>
                  <TableCell>
                    <Chip label={getFieldTypeLabel(field.field_type)} size="small" />
                  </TableCell>
                  <TableCell>
                    {field.is_required ? (
                      <Chip label={t('generic.yes')} color="primary" size="small" />
                    ) : (
                      <Chip label={t('generic.no')} size="small" />
                    )}
                  </TableCell>
                  <TableCell>{field.unit || '-'}</TableCell>
                  <TableCell>
                    {field.field_options ? field.field_options.join(', ') : '-'}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleEdit(field)} color="primary">
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(field)} color="error">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <ModalAddCustomField
        open={openAddModal}
        onClose={handleCloseAddModal}
        maintenanceId={maintenanceId}
        editField={editField}
        refetch={refetch}
      />

      <ModalDelete
        open={openDeleteModal}
        handleClose={() => setOpenDeleteModal(false)}
        message={t('customFields.deleteConfirm', { name: deleteField?.field_name })}
        onClick={confirmDelete}
      />
    </Box>
  );
}
